import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";

export function useVoice(personaId = "default", learningStage = null) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [voiceProvider, setVoiceProvider] = useState("browser");
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    api.get("/tts/status").then(res => setVoiceProvider(res.data.provider)).catch(() => setVoiceProvider("browser"));
  }, []);

  // Get or create AudioContext (stays unlocked after first user gesture)
  const getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const stop = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch { /* already stopped */ }
      sourceRef.current = null;
    }
    if (typeof speechSynthesis !== "undefined") speechSynthesis.cancel();
    setIsSpeaking(false);
    setIsLoading(false);
  }, []);

  // Browser Speech fallback
  const speakWithBrowser = useCallback((text) => {
    if (typeof speechSynthesis === "undefined") return;
    speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.rate = 0.85;
    utterance.pitch = 0.95;
    const voices = speechSynthesis.getVoices();
    const deVoice = voices.find(v => v.lang.startsWith("de") && v.name.includes("Google")) || voices.find(v => v.lang.startsWith("de"));
    if (deVoice) utterance.voice = deVoice;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    speechSynthesis.speak(utterance);
  }, []);

  // Decode base64 data URL to ArrayBuffer
  const decodeAudioData = useCallback(async (dataUrl) => {
    const base64 = dataUrl.split(",")[1];
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }, []);

  // ElevenLabs TTS — using AudioContext for reliable playback (especially iOS)
  const speakWithElevenLabs = useCallback(async (text) => {
    setIsLoading(true);
    try {
      const res = await api.post("/tts/generate", { text, persona_id: personaId, learning_stage: learningStage });
      if (res.data.provider === "elevenlabs" && res.data.audio) {
        const ctx = getAudioCtx();

        // Stop any currently playing source
        if (sourceRef.current) {
          try { sourceRef.current.stop(); } catch {}
          sourceRef.current = null;
        }

        // Decode base64 audio → ArrayBuffer → AudioBuffer
        const arrayBuffer = await decodeAudioData(res.data.audio);
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

        // Create source and play through AudioContext (iOS-reliable)
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);

        source.onended = () => {
          setIsSpeaking(false);
          sourceRef.current = null;
        };

        sourceRef.current = source;
        setIsSpeaking(true);
        setIsLoading(false);
        source.start(0);
        return true;
      }
    } catch (e) {
      console.warn("[Voice] ElevenLabs TTS error:", e.message || e);
    }
    setIsLoading(false);
    return false;
  }, [personaId, learningStage, getAudioCtx, decodeAudioData]);

  const speak = useCallback(async (text) => {
    if (!text) return;
    stop();
    if (voiceProvider === "elevenlabs") {
      const success = await speakWithElevenLabs(text);
      if (!success) speakWithBrowser(text);
    } else {
      speakWithBrowser(text);
    }
  }, [voiceProvider, speakWithElevenLabs, speakWithBrowser, stop]);

  return { speak, stop, isSpeaking, isLoading, voiceProvider };
}
